import * as React from 'react';
import { Component } from 'react';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import formatter from '../utils/formatter';
import BarChart from 'recharts/es6/chart/BarChart';
import Bar from 'recharts/es6/cartesian/Bar';
import XAxis from 'recharts/es6/cartesian/XAxis';
import YAxis from 'recharts/es6/cartesian/YAxis';
import CartesianGrid from 'recharts/es6/cartesian/CartesianGrid';
import ReferenceLine from 'recharts/es6/cartesian/ReferenceLine';
import ResponsiveContainer from 'recharts/es6/component/ResponsiveContainer';
import Tooltip from 'recharts/es6/component/Tooltip';
import Legend from 'recharts/es6/component/Legend';
import VerticalBarLabel from './VerticalBarLabel';
import { computed } from 'mobx';
import { roundNumber } from '@kurtosys/ksys_utils/dist/number';
import { TickHandler } from '../utils/TickHandler';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';

const ResponsiveChartContainer = styled<any, any>(ResponsiveContainer)`
    ${propStyles};
`;

ResponsiveChartContainer.defaultProps = {
    fontSize: 'xs',
};

@inject('dataStore')
@observer
class ChartBarChart extends Component<any, any> {
    private yAxisId = 'left';

    @computed
    get config() {
        const { isMobile, config, mobileConfig } = this.props;
        if (isMobile) {
            return { ...config, ...mobileConfig };
        }
        return config;
    }

    @computed
    get barChartMobileProps() {
        const { isMobile } = this.props;
        const { config } = this;
        const { barChart } = config;
        if (isMobile && barChart) {
            return { ...barChart };
        }
    }

    @computed
    get maxBarSize() {
        const { isMobile } = this.props;
        const { config } = this;
        const { barChart } = config;

        if (!isMobile && barChart && barChart.maxBarSize) {
            return { maxBarSize: barChart.maxBarSize };
        }
    }

    @computed
    get referenceLines() {
        const { isMobile } = this.props;
        const { config } = this;
        const { barChart } = config;

        if (
            barChart &&
            barChart.referenceLines &&
            barChart.referenceLines.length > 0
        ) {
            const referenceLineComponents: Array<JSX.Element> = [];
            barChart.referenceLines.forEach(referenceLine => {
                const y = referenceLine.y || 0;
                // Flip reference lines axis for vertical bar chart in mobile view
                const axis = isMobile ? { x: y } : { y };
                const referenceLineComponent = (
                    <ReferenceLine
                        key={referenceLine.y}
                        yAxisId={this.yAxisId}
                        stroke={referenceLine.stroke || 'black'}
                        strokeWidth={referenceLine.strokeWidth || 1}
                        {...axis}
                    />
                );
                referenceLineComponents.push(referenceLineComponent);
            });

            return referenceLineComponents;
        }
    }

    seriesHasData(key: string) {
        const { data } = this.props;
        if (!isNullOrEmpty(data)) {
            if (Array.isArray(data)) {
                return (
                    data.filter(item => !isNullOrEmpty(item[key])).length > 0
                );
            }
            return !isNullOrEmpty(data[key]);
        }
        return false;
    }

    @computed
    get series() {
        const { series, showBlankSeries = false } = this.props;
        if (
            !showBlankSeries &&
            !isNullOrEmpty(series) &&
            Array.isArray(series)
        ) {
            return series.filter(s => this.seriesHasData(s.dataKey));
        }
        return series;
    }

    @computed
    get yDomain() {
        const { yAxis, series, data } = this.props;
        if (yAxis && yAxis.domain) {
            return yAxis.domain;
        }
        const values = [];
        // Get values from each series data key for min and max
        series.forEach(element => {
            data.map(row => {
                const value = row[element.dataKey];
                if (value) {
                    values.push(value);
                }
            });
        });

        const min = Math.min(...values);
        const max = Math.max(...values);

        return [min < 0 ? Math.floor(min) : 0, max < 0 ? 0 : Math.ceil(max)];
    }

    @computed
    get seriesDataKeys() {
        const { series } = this;
        if (Array.isArray(series)) {
            return series.map(item => item.dataKey);
        }
        return [];
    }

    applyTickPivotPoint(element: XAxis | YAxis) {
        const { tickPivotPoint } = element;
        if (typeof tickPivotPoint === 'number') {
            const { data } = this.props;
            const tickHandler = new TickHandler();
            element.ticks = tickHandler.getTicksForSeries(
                data,
                this.seriesDataKeys,
                tickPivotPoint
            );
        }
    }

    render() {
        const {
            theme,
            dataStore,
            data,
            xAxis,
            yAxis,
            layout,
            minHeight,
            aspect,
            getThemeColor,
            getLabelName,
            getSeriesName,
            locale,
            fundCurrency,
            isMobile,
        } = this.props;
        const {
            barChartMobileProps,
            maxBarSize,
            yAxisId,
            referenceLines,
            yDomain,
            config,
        } = this;

        const { label: labelConfig } = config;
        const labelPrecision = labelConfig && labelConfig.precision;
        let xAxisLeftPadding = 10;

        if (isMobile && labelConfig) {
            let maxLength = 0;

            for (const item of data.filter(d => d.value < 0)) {
                let { value } = item;
                if (labelPrecision) {
                    value = roundNumber(value, labelPrecision, false);
                }
                const length = `${value}`.length;
                maxLength = Math.max(maxLength, length);
            }

            if (maxLength > 0) {
                xAxisLeftPadding += maxLength * labelConfig.spacing;
            }
        }

        const xAxisPadding = xAxis.padding || {
            left: xAxisLeftPadding,
            right: xAxisLeftPadding,
        };

        this.applyTickPivotPoint(xAxis);
        this.applyTickPivotPoint(yAxis);

        return (
            <ResponsiveChartContainer
                aspect={aspect}
                minHeight={minHeight}
                {...theme.ResponsiveChartContainer}
            >
                <BarChart
                    data={data}
                    layout={layout}
                    margin={
                        layout === 'vertical'
                            ? { top: 5, right: 30, bottom: 5, left: 0 }
                            : {}
                    }
                    {...barChartMobileProps}
                    {...maxBarSize}
                >
                    {config.grid && (
                        <CartesianGrid
                            {...config.grid}
                            vertical={
                                isMobile
                                    ? config.grid.horizontal
                                    : config.grid.vertical
                            }
                            horizontal={
                                isMobile
                                    ? config.grid.vertical
                                    : config.grid.horizontal
                            }
                            stroke={getThemeColor(config.grid.stroke)}
                            fill={getThemeColor(config.grid.fill)}
                        />
                    )}
                    {!isMobile && config.tooltip && (
                        <Tooltip
                            cursor={false}
                            {...config.tooltip}
                            labelFormatter={v =>
                                config.tooltip.labelFormat
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: config.tooltip.labelFormat,
                                          type: config.tooltip.labelFormat.type,
                                          currency: fundCurrency,
                                          options:
                                              config.tooltip.labelFormat
                                                  .options,
                                          value: v,
                                      })
                                    : v
                            }
                            formatter={v =>
                                config.tooltip.valueFormat
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: config.tooltip.valueFormat,
                                          type: config.tooltip.valueFormat.type,
                                          currency: fundCurrency,
                                          options:
                                              config.tooltip.valueFormat
                                                  .options,
                                          value: v,
                                      })
                                    : v
                            }
                        />
                    )}
                    {config.legend && (
                        <Legend
                            iconType="circle"
                            wrapperStyle={{ bottom: 0 }}
                            {...config.legend}
                        />
                    )}
                    {xAxis && (
                        <XAxis
                            dataKey={xAxis.dataKey}
                            tickLine={false}
                            padding={xAxisPadding}
                            {...xAxis}
                            label={{
                                ...getLabelName(xAxis.label),
                                ...{
                                    offset: 0,
                                    angle: 0,
                                    position: 'insideBottom',
                                },
                            }}
                            tickFormatter={v =>
                                xAxis.format
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: xAxis.format,
                                          type: xAxis.format.type,
                                          currency: fundCurrency,
                                          options: xAxis.format.options,
                                          value: v,
                                      })
                                    : v
                            }
                            domain={isMobile && yDomain}
                        />
                    )}
                    {yAxis && (
                        <YAxis
                            dataKey={yAxis.dataKey}
                            yAxisId={yAxisId}
                            orientation="left"
                            axisLine={false}
                            tickLine={false}
                            {...yAxis}
                            label={getLabelName(yAxis.label)}
                            tickFormatter={v =>
                                yAxis.format
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: yAxis.format,
                                          type: yAxis.format.type,
                                          currency: fundCurrency,
                                          options: yAxis.format.options,
                                          value: v,
                                      })
                                    : v
                            }
                            domain={!isMobile && yDomain}
                        />
                    )}
                    {this.series.map((s, index) => (
                        <Bar
                            key={`bar-${s.dataKey}-${index}`}
                            dataKey={s.dataKey}
                            yAxisId={yAxisId}
                            {...s}
                            name={getSeriesName(s)}
                            label={
                                isMobile ? (
                                    <VerticalBarLabel
                                        {...s.label}
                                        precision={labelPrecision}
                                    />
                                ) : (
                                    false
                                )
                            }
                            fill={getThemeColor(s.fill)}
                            isAnimationActive={!dataStore.isExpandAll}
                        />
                    ))}
                    {...referenceLines}
                </BarChart>
            </ResponsiveChartContainer>
        );
    }
}

export default withTheme(ChartBarChart) as any;



// WEBPACK FOOTER //
// ./src/Components/ChartBarChart.tsx